// Категория: чистые константы и хелперы без побочных эффектов и без зависимостей.
//
// Модуль намеренно ни от чего не зависит. Значения ниже нужны сразу трём слоям:
// api/convert.js, tools/ga.js и обоим компонентам выдачи.
//
// Почему отдельный файл, а не downloadActions.js рядом с самим скачиванием:
// downloadActions.js импортирует analytics из ga.js. Держи константы там — и ga.js
// пришлось бы импортировать downloadActions.js, замкнув
// downloadActions -> ga -> downloadActions. Сейчас такого цикла нет, связь
// односторонняя (downloadActions -> ga), и именно это разделение её сохраняет.
//
// Почему не в ga.js: его импортировал бы api/convert.js ради одной константы —
// сетевой слой потянул бы модуль, завязанный на window.gtag.
//
// Отсюда правило: у этого файла не должно появиться ни одного импорта.

// Ветка, по которой ушло скачивание. Вызывающие сравнивают результат с этими
// константами, а не со строковыми литералами.
// Значения в snake_case, как и в NO_FORMATS_REASON ниже: константы связаны
// маппингом resultToFailureReason(), и разный регистр у одноимённых членов
// («no-converter» против «no_converter») мешал сравнивать их глазами и грепом.
export const DOWNLOAD_RESULT = {
    CONVERTER_UI: 'converter_ui',
    LOCAL_CONVERTER: 'local_converter',
    DIRECT: 'direct',
    // ссылка не прошла проверку схемы
    BLOCKED: 'blocked',
    // формат требует клиентского мёржа, но внешний LocalConverter недоступен
    NO_CONVERTER: 'no_converter',
    // стратегия скачивания бросила исключение — скачивание не началось
    FAILED: 'failed',
};

// Причина, по которой скачивать нечего. Читается в ga.js (метки событий),
// в api/convert.js и в обоих компонентах.
export const NO_FORMATS_REASON = {
    EMPTY: 'empty',
    MULTIPLE: 'multiple',
    BLOCKED: 'blocked',
    NO_CONVERTER: 'no_converter',
    // скачивание не удалось запустить из-за исключения на клиенте
    FAILED: 'failed',
    // сервер ответил формой, которую мы не умеем читать. Отделено от EMPTY:
    // «предложить нечего» и «ответ битый» — разные сбои, и в одной метке
    // отличить сломанный бэкенд от штатно пустой выдачи было нельзя
    MALFORMED: 'malformed',
};

// Код ошибки «не дождались слота в пуле запросов». Обычный Error здесь не годится:
// без опознавательного признака отказ очереди доходил до ga.js неразобранным и
// тонул в общей метке 'fatal' вместе с настоящими сбоями.
export const QUEUE_TIMEOUT_CODE = 'EQUEUETIMEOUT';

// Код отказа «подпись недоступна, запрос не отправляем». Ставит его
// tools/signedRequestClient.js, а читает buildConvertErrorResult ниже — значение
// одно на оба места, иначе расхождение молча сломало бы распознавание fail-closed.
export const SIGNING_UNAVAILABLE_CODE = 'ESIGNINGUNAVAILABLE';

/**
 * Приводит отказ «ответ пришёл, но предложить нечего» к контракту AnalyticsErrorResult.
 * ga.js#showError не находит в таком теле ни одного своего поля: без явного маркера
 * отказ дошёл бы до воронки через catch-all, но обезличенно — как 'unclassified'.
 * @param data {*} - распарсенное тело ответа
 * @param reason {string} - значение NO_FORMATS_REASON
 * @returns {import('./ga').AnalyticsErrorResult}
 */
export function buildNoFormatsResult(data, reason) {
    const body = data && typeof data === 'object' && !Array.isArray(data) ? data : {};
    return { ...body, no_formats: reason };
}

/**
 * Приводит ошибку запроса к объекту, который понимает ga.js#showError.
 * Тот читает два набора полей: бизнес-поля тела ответа (supported_resource / success /
 * code / blacklist) и поля axios-ответа (status, data.error_code для 401) — отдаём оба,
 * иначе часть ошибок схлопывается в общий 'link_not_found'.
 *
 * data нормализуется до объекта, чтобы форма была одинаковой даже когда сервер
 * ответил пустым или не-JSON телом.
 *
 * Набор полей задан typedef AnalyticsErrorResult в tools/ga.js — правятся вместе.
 * @param e {Error} - ошибка axios или отказ пула запросов
 * @returns {import('./ga').AnalyticsErrorResult|null}
 */
export function buildConvertErrorResult(e) {
    // отказ очереди — не сетевая ошибка и не таймаут самого запроса: у него своя
    // метка, иначе перегруз пула был бы неотличим от падения бэкенда
    if (e.code === QUEUE_TIMEOUT_CODE) {
        return { queue_timeout: true };
    }
    // осознанный fail-closed анти-abuse, а не сбой: без своей метки он был бы
    // неотличим в аналитике от любой неопознанной ошибки
    if (e.code === SIGNING_UNAVAILABLE_CODE) {
        return { signing_unavailable: true };
    }
    if (e.code === 'ECONNABORTED') {
        return { timeout: true };
    }
    if (!e.response) {
        return null;
    }

    const { status, data } = e.response;
    const body = data && typeof data === 'object' ? data : {};
    return { ...body, status, data: body };
}
